import { HttpParams } from '@angular/common/http';
import { date_convertToString } from './date.util';

/**
 * Recursively flattens an object or array and converts it into HttpParams.
 * Handles nested arrays, objects, and removes null/undefined values.
 * @param paramsObject Object containing query parameters
 * @returns HttpParams instance
 */
export function http_toParams(paramsObject: Record<string, unknown>): {
  params: HttpParams;
} {
  let params = new HttpParams();

  const processKeyValue = (key: string, value: unknown) => {
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      // Skip null or undefined values
      return;
    }

    if (Array.isArray(value)) {
      // Handle arrays: Recursively process each item
      value.forEach((item, index) => {
        processKeyValue(`${key}[${index}]`, item);
      });
    } else if (value instanceof Date) {
      // Handle Date instances before checking generic objects
      params = params.append(key, date_convertToString(value));
    } else if (typeof value === 'object') {
      // Handle nested objects: Flatten keys
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        processKeyValue(`${key}.${nestedKey}`, nestedValue);
      });
    } else {
      // Append primitive values
      params = params.append(key, String(value));
    }
  };

  // Process the initial object
  Object.entries(paramsObject).forEach(([key, value]) =>
    processKeyValue(key, value),
  );

  return { params };
}

/**
 * Recursively transforms various data types into a format suitable for HTTP requests.
 *
 * @param data The data to be transformed.
 * @returns The transformed data in a format suitable for HTTP requests.
 */

// Ignore performance issues. Because in normal cases, body allways simple object
export const http_transformBody = (data: unknown): unknown => {
  if (data instanceof FormData) return data;

  // Preserve File and Blob types to prevent breaking binary structures
  if (data instanceof File || data instanceof Blob) return data;

  if (data instanceof Date) return date_convertToString(data);

  if (Array.isArray(data)) {
    return data.map((item) => http_transformBody(item));
  }

  if (typeof data === 'object' && data !== null) {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        http_transformBody(value),
      ]),
    );
  }

  return data;
};
